import { createAsyncThunk } from '@reduxjs/toolkit';
import QueryString from 'qs';
import { TApiaFinderFilter, TAsyncThunkState } from '../../../types';
import { notify } from '../../../../../components/common/notifications/util';
import ApiaApi, { makeApiaUrl } from '../../../../../api/apiaApi/apiaApi';
import { queryActions } from '../../..';
import { validateDateFilters } from './search';
import { formatMessage } from '../../../../../utils/util';
import { TQueriesActionsChain } from '../../../queueController';

export type TUpdateFilters = {
  fieldId: string;
  filters?: TApiaFinderFilter[];
  onlyRedux?: boolean;
  avoidNotify?: boolean;
  queueKey?: TQueriesActionsChain;
};

const setFilters = createAsyncThunk<
  { filters: TApiaFinderFilter[]; appliedFilters?: boolean } | undefined,
  TUpdateFilters,
  TAsyncThunkState
>(
  'querySlice/setFilters',
  async ({ fieldId, filters, onlyRedux, avoidNotify }, thunkApi) => {
    const state = thunkApi.getState().querySlice.queries[fieldId];
    const { dispatch } = thunkApi;

    /**
     * La siguiente logica se encarga de detecctar si todos los filtros
     * requeridos fueron ingresados. De ser asi o si no hay filtros requeridos,
     * isRequirementSet deberia cambiar a true
     */
    let isRequirementSet = false;
    let required = 0;
    let withValue = 0;
    const emptyFilters: string[] = [];

    if (filters) {
      state.filters?.forEach((filter) => {
        if (filter.apiaFilter.required) {
          required++;
          if (
            filter.apiaFilter.currentValue !== '' ||
            filters.findIndex((innerFilter) => {
              return innerFilter.apiaFilter.id === filter.apiaFilter.id;
            }) !== -1
          ) {
            withValue++;
          } else {
            emptyFilters.push(
              (filter.apiaFilter.title ?? filter.apiaFilter.id) as string,
            );
          }
        }
      });
      filters.forEach((filter) => {
        if (filter.apiaFilter.required) {
          required++;
          if (filter.apiaFilter.currentValue !== '') {
            withValue++;
          } else {
            emptyFilters.push(
              (filter.apiaFilter.title ?? filter.apiaFilter.id) as string,
            );
          }
        }
      });
    }
    if (required === withValue) isRequirementSet = true;

    const areDatesValid = validateDateFilters(state.filters ?? []);
    /**
     * La siguiente logica se encarga de guardar en newFilters cada filtro en
     * el estado con los cambios de valor realizados
     */

    const newFilters = filters
      ? state.filters?.map((currentFilter) => {
          let updatingFilter: TApiaFinderFilter | undefined = filters.find(
            (searchFilter) => {
              if (
                searchFilter.apiaFilter.id ===
                currentFilter.apiaFilter.filterToId
              ) {
                return (
                  searchFilter.apiaFilter.id ===
                  currentFilter.apiaFilter.filterToId
                );
              }

              return searchFilter.apiaFilter.id === currentFilter.apiaFilter.id;
            },
          );
          if (updatingFilter) {
            if (
              currentFilter.apiaFilter.isRange &&
              updatingFilter.apiaFilter.id ===
                currentFilter.apiaFilter.filterToId
            ) {
              if (updatingFilter.apiaFilter.id === 'txtSizeTo') {
                updatingFilter.apiaFilter.filterToValue =
                  updatingFilter.apiaFilter.currentValue;
                updatingFilter.apiaFilter.currentValue =
                  currentFilter.apiaFilter.currentValue;
                updatingFilter.apiaFilter.filterToId =
                  currentFilter.apiaFilter.filterToId;
                updatingFilter.apiaFilter.id = currentFilter.apiaFilter.id;
              }
            } else if (updatingFilter.apiaFilter.options) {
              updatingFilter.apiaFilter.options =
                updatingFilter.apiaFilter.options.map((option) => {
                  if (
                    updatingFilter!.apiaFilter.currentValue === option.value
                  ) {
                    return {
                      ...option,
                      selected: true,
                    };
                  }
                  return {
                    ...option,
                    selected: false,
                  };
                });
              updatingFilter.apiaFilter.error =
                !!updatingFilter.apiaFilter.currentValue &&
                currentFilter.apiaFilter.error === 'Filtro requerido'
                  ? undefined
                  : currentFilter.apiaFilter.error;
            } else if (
              ['date', 'D'].includes(currentFilter.apiaFilter.type as string)
            ) {
              updatingFilter = {
                ...currentFilter,
                apiaFilter: {
                  ...currentFilter.apiaFilter,
                  currentValue: updatingFilter.apiaFilter.currentValue,
                  filterToValue: updatingFilter.apiaFilter.filterToValue,
                  deleteFiltersTimestamp:
                    updatingFilter.apiaFilter.deleteFiltersTimestamp,
                  changeFiltersTimestamp:
                    updatingFilter.apiaFilter.changeFiltersTimestamp,
                  errorFrom:
                    !!updatingFilter.apiaFilter.currentValue &&
                    currentFilter.apiaFilter.errorFrom === 'Filtro requerido' &&
                    updatingFilter.apiaFilter.errorFrom === undefined
                      ? undefined
                      : updatingFilter.apiaFilter.errorFrom
                        ? updatingFilter.apiaFilter.errorFrom
                        : !updatingFilter.apiaFilter.currentValue &&
                            currentFilter.apiaFilter.errorFrom ===
                              'Filtro requerido'
                          ? currentFilter.apiaFilter.errorFrom
                          : undefined,
                  errorTo:
                    !!updatingFilter.apiaFilter.filterToValue &&
                    currentFilter.apiaFilter.errorTo === 'Filtro requerido' &&
                    updatingFilter.apiaFilter.errorTo === undefined
                      ? undefined
                      : updatingFilter.apiaFilter.errorTo
                        ? updatingFilter.apiaFilter.errorTo
                        : !updatingFilter.apiaFilter.filterToValue &&
                            currentFilter.apiaFilter.errorTo ===
                              'Filtro requerido'
                          ? currentFilter.apiaFilter.errorTo
                          : undefined,
                  error:
                    !!updatingFilter.apiaFilter.filterToValue &&
                    !!updatingFilter.apiaFilter.currentValue &&
                    currentFilter.apiaFilter.error === 'Filtro requerido' &&
                    updatingFilter.apiaFilter.error === undefined
                      ? undefined
                      : updatingFilter.apiaFilter.error
                        ? updatingFilter.apiaFilter.error
                        : !(
                              !!updatingFilter.apiaFilter.filterToValue &&
                              !!updatingFilter.apiaFilter.currentValue
                            ) &&
                            currentFilter.apiaFilter.error ===
                              'Filtro requerido'
                          ? currentFilter.apiaFilter.error
                          : undefined,
                  savedValue: updatingFilter.apiaFilter.savedValue
                    ? updatingFilter.apiaFilter.savedValue
                    : currentFilter.apiaFilter.savedValue,
                },
              };
            } else {
              updatingFilter = {
                ...currentFilter,
                apiaFilter: {
                  ...currentFilter.apiaFilter,
                  currentValue: updatingFilter.apiaFilter.currentValue,
                  deleteFiltersTimestamp:
                    updatingFilter.apiaFilter.deleteFiltersTimestamp,
                  changeFiltersTimestamp:
                    updatingFilter.apiaFilter.changeFiltersTimestamp,
                  error:
                    !!updatingFilter.apiaFilter.currentValue &&
                    currentFilter.apiaFilter.error === 'Filtro requerido'
                      ? undefined
                      : currentFilter.apiaFilter.error,
                  savedValue: updatingFilter.apiaFilter.savedValue
                    ? updatingFilter.apiaFilter.savedValue
                    : currentFilter.apiaFilter.savedValue,
                },
              };
            }

            return {
              ...currentFilter,
              ...updatingFilter,
              apiaFilter: {
                ...currentFilter.apiaFilter,
                ...updatingFilter.apiaFilter,
                changeFiltersTimestamp: Date.now(),
              },
            };
          }

          return {
            ...currentFilter,
            apiaFilter: {
              ...currentFilter.apiaFilter,
              changeFiltersTimestamp: Date.now(),
            },
          };
        })
      : state.filters;

    /**
     * La siguiente logica se encarga de armar el payload que se enviara para
     * settear los filtros en el bean
     */

    const payloadFilters: Record<string, unknown> = {};

    newFilters?.forEach((currentFilter) => {
      payloadFilters[`filter${currentFilter.apiaFilter.id}`] =
        currentFilter.apiaFilter.currentValue;
      if (currentFilter.apiaFilter.options) {
        currentFilter.apiaFilter.options.forEach((option) => {
          if (option.selected) {
            payloadFilters[`filter${currentFilter.apiaFilter.id}`] =
              option.value;
          }
        });
      }
      if (
        (['date', 'D'].includes(currentFilter.apiaFilter.type as string) ||
          currentFilter.apiaFilter.isRange) &&
        currentFilter.apiaFilter.filterToId &&
        currentFilter.apiaFilter.filterToValue
      ) {
        payloadFilters[`filter${currentFilter.apiaFilter.filterToId}`] =
          currentFilter.apiaFilter.filterToValue;
      }
    });

    if (!onlyRedux) {
      if (isRequirementSet && areDatesValid) {
        await ApiaApi.post(
          makeApiaUrl({
            action: 'setFilters',
            preventAsXmlParameter: true,
            isAjax: true,
            workingMode: fieldId === 'taskListQueryFreeTasks' ? 'R' : 'I',
          }),
          {
            postData: QueryString.stringify(payloadFilters),
          },
        );
        void dispatch(
          queryActions.setState({
            fieldId,
            filters: newFilters,
          }),
        );
      } else {
        if (!areDatesValid) {
          notify({
            message: window.MSG_FEC_FIN_MAY_FEC_INI,
            type: 'warning',
          });
          return { filters: newFilters ?? [], appliedFilters: areDatesValid };
        } else {
          if (!avoidNotify) {
            notify({
              message: formatMessage(window.LBL_FILTER_REQUIRED_LIST_QUERY, {
                TOK1: emptyFilters.join(', '),
              }),
              type: 'warning',
            });
          }
          void dispatch(
            queryActions.setState({
              fieldId,
              rows: [],
            }),
          );
        }
      }

      return {
        filters: newFilters ?? [],
        appliedFilters:
          // areDatesValid &&
          !newFilters?.some(
            (f) =>
              (f.apiaFilter.type === 'D' || f.apiaFilter.type === 'date') &&
              (f.apiaFilter.error ||
                f.apiaFilter.errorFrom ||
                f.apiaFilter.errorTo),
          ),
      };
    }
    return {
      filters: newFilters ?? [],
      appliedFilters: !!state.filtersConfig?.runFiltersAutomatically,
    };
  },
);

export default setFilters;
